<template>
  <div class="device-management">
    <div class="page-header">
      <h2>设备管理</h2>
      <p>管理所有设备信息</p>
    </div>
    
    <div class="page-content">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-box">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="请输入设备名称或编码搜索"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <select v-model="searchStoreId" class="store-select">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">
              {{ store.storeName }}
            </option>
          </select>
          <select
            v-model="searchDeviceType"
            class="device-type-select"
            :disabled="!searchStoreId"
            :title="!searchStoreId ? '请先选择具体门店后再按设备类型筛选' : ''"
          >
            <option value="">{{ searchStoreId ? '全部类型' : '请先选择门店' }}</option>
            <option v-for="type in deviceTypeOptions" :key="type.code" :value="type.code">
              {{ type.name }}
            </option>
          </select>
          <button @click="handleSearch" class="search-btn">搜索</button>
          <button @click="handleReset" class="reset-btn">重置</button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-header">
          <h3>设备列表</h3>
          <button @click="handleAdd" class="add-btn">新增设备</button>
        </div>
        
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>设备编码</th>
                <th>设备名称</th>
                <th>设备类型</th>
                <th>所属门店</th>
                <th>描述</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="8" class="loading-cell">
                  <div class="loading-spinner">
                    <div class="spinner"></div>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!deviceList || deviceList.length === 0" class="empty-row">
                <td colspan="8" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-else v-for="device in deviceList" :key="device.id" class="data-row">
                <td>{{ device.deviceCode }}</td>
                <td>{{ device.deviceName }}</td>
                <td>{{ getDeviceTypeName(device) }}</td>
                <td>{{ getStoreName(device.storeId) }}</td>
                <td>{{ device.description }}</td>
                <td>
                  <span :class="['status-badge', device.status ? 'active' : 'inactive']">
                    {{ device.status ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>{{ formatDate(device.createdTime) }}</td>
                <td>
                  <button @click="handleEdit(device)" class="action-btn edit-btn">编辑</button>
                  <button
                    v-if="isSocketDevice(device)"
                    @click="handleSocketConfig(device)"
                    class="action-btn socket-btn"
                  >
                    插座配置
                  </button>
                  <button @click="handleDelete(device)" class="action-btn delete-btn">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页区域 -->
        <div class="pagination-section" v-if="!loading && deviceList && deviceList.length > 0">
          <div class="pagination-info">
            共 {{ pagination.totalElements }} 条记录，第 {{ pagination.number + 1 }} / {{ pagination.totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button 
              @click="handlePageChange(0)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              首页
            </button>
            <button 
              @click="handlePageChange(pagination.number - 1)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              上一页
            </button>
            <span class="page-info">
              {{ pagination.number + 1 }} / {{ pagination.totalPages }}
            </span>
            <button 
              @click="handlePageChange(pagination.number + 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              下一页
            </button>
            <button 
              @click="handlePageChange(pagination.totalPages - 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              末页
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 新增设备弹窗 -->
    <AddDeviceModal 
      :visible="showAddModal" 
      :store-list="storeList"
      @close="handleCloseAddModal"
      @success="handleAddSuccess"
    />
    
    <!-- 编辑设备弹窗 -->
    <EditDeviceModal 
      :visible="showEditModal" 
      :device-data="editDeviceData"
      :store-list="storeList"
      @close="handleCloseEditModal"
      @success="handleEditSuccess"
    />

    <EditSocketConfigModal
      :visible="showSocketModal"
      :device-data="socketDeviceData"
      @close="handleCloseSocketModal"
      @success="handleSocketSuccess"
    />
  </div>
</template>

<script>
import request from '../utils/request'
import AddDeviceModal from './AddDeviceModal.vue'
import EditDeviceModal from './EditDeviceModal.vue'
import EditSocketConfigModal from './EditSocketConfigModal.vue'

export default {
  name: 'DeviceManagement',
  components: {
    AddDeviceModal,
    EditDeviceModal,
    EditSocketConfigModal
  },
  data() {
    return {
      deviceList: [],
      storeList: [],
      deviceTypeOptions: [],
      deviceTypeOptionsByStore: {},
      loading: false,
      searchKeyword: '',
      searchStoreId: '',
      searchDeviceType: '',
      showAddModal: false,
      showEditModal: false,
      showSocketModal: false,
      editDeviceData: null,
      socketDeviceData: null,
      pagination: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      }
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadDeviceList()
  },
  watch: {
    searchStoreId(newVal) {
      this.loadDeviceTypeOptions(newVal)
    }
  },
  methods: {
    async loadStoreList() {
      try {
        const response = await request.get('/api/stores/getStores', {
          params: {
            page: 0,
            size: 1000 // 获取所有门店用于下拉框
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.storeList = response.data.data.content || []
          this.loadAllStoreDeviceTypeOptions()
        }
      } catch (error) {
        console.error('获取门店列表失败:', error)
      }
    },

    async loadDeviceList() {
      this.loading = true
      try {
        const response = await request.get('/api/devices/getDevices', {
          params: {
            page: this.pagination.number,
            size: this.pagination.size,
            keyword: this.searchKeyword || undefined,
            storeId: this.searchStoreId || undefined,
            deviceType: this.searchDeviceType || undefined
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.deviceList = response.data.data.content || []
          this.pagination = {
            number: response.data.data.number || 0,
            size: response.data.data.size || 20,
            totalElements: response.data.data.totalElements || 0,
            totalPages: response.data.data.totalPages || 0,
            first: response.data.data.first || true,
            last: response.data.data.last || true
          }
        } else {
          throw new Error(response.data.message || '获取设备列表失败')
        }
      } catch (error) {
        console.error('获取设备列表失败:', error)
        let errorMessage = '获取设备列表失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '获取设备列表失败，请重试'
        }
        
        alert(errorMessage)
        this.deviceList = []
        // 重置分页信息
        this.pagination = {
          number: 0,
          size: 20,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true
        }
      } finally {
        this.loading = false
      }
    },

    async loadDeviceTypeOptions(storeId = this.searchStoreId) {
      if (!storeId) {
        this.searchDeviceType = ''
        this.deviceTypeOptions = []
        return
      }
      try {
        const response = await request.get('/api/devices/device-types', {
          params: {
            storeId
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.deviceTypeOptions = response.data.data || []
          if (this.searchDeviceType && !this.deviceTypeOptions.some(type => type.code === this.searchDeviceType)) {
            this.searchDeviceType = ''
          }
        } else {
          throw new Error(response.data.message || '获取设备类型失败')
        }
      } catch (error) {
        console.error('获取设备类型失败:', error)
        this.deviceTypeOptions = []
      }
    },

    async loadAllStoreDeviceTypeOptions() {
      const stores = this.storeList || []
      await Promise.all(stores.map(store => this.loadStoreDeviceTypeOptions(store.id)))
      if (!this.searchStoreId) {
        this.deviceTypeOptions = []
      }
    },

    async loadStoreDeviceTypeOptions(storeId) {
      if (!storeId) {
        return
      }
      try {
        const response = await request.get('/api/devices/device-types', {
          params: {
            storeId
          }
        })

        if (response.data.success && response.data.code === 200) {
          const options = response.data.data || []
          this.$set
            ? this.$set(this.deviceTypeOptionsByStore, String(storeId), options)
            : (this.deviceTypeOptionsByStore[String(storeId)] = options)
          return options
        }
      } catch (error) {
        console.error(`获取门店 ${storeId} 设备类型失败:`, error)
      }
      return []
    },

    handleSearch() {
      this.pagination.number = 0 // 重置到第一页
      this.loadDeviceList()
    },

    handleReset() {
      this.searchKeyword = ''
      this.searchStoreId = ''
      this.searchDeviceType = ''
      this.pagination.number = 0
      this.loadDeviceList()
    },

    handlePageChange(pageNumber) {
      if (pageNumber >= 0 && pageNumber < this.pagination.totalPages) {
        this.pagination.number = pageNumber
        this.loadDeviceList()
      }
    },

    handleAdd() {
      this.showAddModal = true
    },
    
    handleCloseAddModal() {
      this.showAddModal = false
    },
    
    handleAddSuccess() {
      // 新增成功后刷新列表
      this.loadAllStoreDeviceTypeOptions()
      this.loadDeviceList()
    },

    handleEdit(device) {
      this.editDeviceData = device
      this.showEditModal = true
    },
    
    handleCloseEditModal() {
      this.showEditModal = false
      this.editDeviceData = null
    },
    
    handleEditSuccess() {
      // 编辑成功后刷新列表
      this.loadAllStoreDeviceTypeOptions()
      this.loadDeviceList()
    },

    handleSocketConfig(device) {
      this.socketDeviceData = device
      this.showSocketModal = true
    },

    handleCloseSocketModal() {
      this.showSocketModal = false
      this.socketDeviceData = null
    },

    handleSocketSuccess(updatedDevice) {
      this.handleCloseSocketModal()
      if (updatedDevice && updatedDevice.id) {
        this.deviceList = this.deviceList.map(item => (item.id === updatedDevice.id ? { ...item, ...updatedDevice } : item))
      } else {
        this.loadDeviceList()
      }
    },

    async handleDelete(device) {
      if (!confirm(`确定要删除设备 "${device.deviceName}" 吗？\n\n此操作不可撤销！`)) {
        return
      }

      try {
        const response = await request.delete('/api/devices/deleteDevice', {
          params: {
            id: device.id
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.loadDeviceList()
        } else {
          throw new Error(response.data.message || '删除设备失败')
        }
      } catch (error) {
        console.error('删除设备失败:', error)
        let errorMessage = '删除设备失败'

        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '删除设备失败，请重试'
        }

        alert(errorMessage)
      }
    },

    getDeviceTypeName(device) {
      const deviceType = device && device.deviceType
      const storeId = device && device.storeId
      const storeOptions = this.deviceTypeOptionsByStore[String(storeId)] || []
      const type = storeOptions.find(item => item.code === deviceType)
        || this.deviceTypeOptions.find(item => item.code === deviceType)
      if (type) {
        return type.name
      }
      return deviceType ? `设备类型${deviceType}` : '-'
    },

    isSocketDevice(device) {
      const deviceType = device && device.deviceType
      const storeId = device && device.storeId
      const storeOptions = this.deviceTypeOptionsByStore[String(storeId)] || []
      const type = storeOptions.find(item => item.code === deviceType)
        || this.deviceTypeOptions.find(item => item.code === deviceType)
      if (type) {
        return Boolean(type.socketType)
      }
      return false
    },

    getStoreName(storeId) {
      const store = this.storeList.find(s => s.id === storeId)
      return store ? store.storeName : '未知门店'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.device-management {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 搜索区域 */
.search-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.search-box {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  flex: 1;
  max-width: 200px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.store-select, .device-type-select {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 120px;
}

.device-type-select:disabled {
  color: #999;
  background: #f5f5f5;
  border-color: #d9d9d9;
  cursor: not-allowed;
}

.store-select:focus, .device-type-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.search-btn, .reset-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.search-btn:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

.reset-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

/* 表格区域 */
.table-section {
  margin-bottom: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.add-btn {
  padding: 8px 16px;
  background: #52c41a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
}

.add-btn:hover {
  background: #73d13d;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background: #fafafa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
}

.data-row:hover {
  background: #f5f5f5;
}

.loading-cell, .empty-cell {
  text-align: center;
  color: #999;
  font-style: italic;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-badge.inactive {
  background: #fff2e8;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.action-btn {
  padding: 4px 8px;
  margin-right: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
}

.edit-btn {
  color: #1890ff;
  border-color: #1890ff;
}

.edit-btn:hover {
  background: #e6f7ff;
  border-color: #40a9ff;
}

.socket-btn {
  color: #722ed1;
  border-color: #722ed1;
}

.socket-btn:hover {
  background: #f9f0ff;
  border-color: #9254de;
}

.delete-btn {
  color: #ff4d4f;
  border-color: #ff4d4f;
}

.delete-btn:hover {
  background: #fff2f0;
  border-color: #ff7875;
}

/* 分页区域 */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.page-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #d9d9d9;
}

.page-info {
  padding: 6px 12px;
  color: #666;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-box {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input, .store-select, .device-type-select {
    max-width: none;
  }
  
  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .pagination-section {
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }
  
  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
